import { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useTopAccounts } from '@/hooks/use-api'
import { useUser } from '@/contexts/UserContext'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { ReportFilters } from '@/components/ReportFilters'
import type { ReportFilterValues } from '@/components/ReportFilters'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts'
import { Download, Building2, DollarSign, Users, TrendingUp, Loader2 } from 'lucide-react'
import { formatCurrency, formatDate } from '@/lib/utils'
import { CHART_COLORS, SEMANTIC, CATEGORICAL } from '@/lib/theme'
import { exportToCSV } from '@/utils/csvExport'
import { ChartTooltipContent } from '@/components/ui/chart-tooltip'
import { explainError } from '@/lib/explain-error'
import { SpendTracker } from '@/components/reports/SpendTracker'

const COLORS = CHART_COLORS

interface TopAccountRow {
  id: string
  name: string
  owner_name: string | null
  opportunity_count: number
  won_count: number
  closed_won_revenue: number
  pipeline_value: number
  win_rate: number | null
}

interface TopAccountsSummary {
  total_accounts: number
  total_revenue: number
  won_deals: number
  top10_revenue: number
  top10_share_pct: number
  avg_revenue_per_account: number
  avg_deal_size: number
}

function periodText(p?: { startDate: string | null; endDate: string | null }) {
  if (!p || (!p.startDate && !p.endDate)) return 'all time'
  if (p.startDate && p.endDate) return `${formatDate(p.startDate)} to ${formatDate(p.endDate)}`
  return p.startDate ? `from ${formatDate(p.startDate)}` : `up to ${formatDate(p.endDate)}`
}

function StatCard({
  title,
  value,
  note,
  icon: Icon,
  color,
}: {
  title: string
  value: string
  note: string
  icon: typeof DollarSign
  color: string
}) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        <Icon className="h-4 w-4" aria-hidden="true" style={{ color }} />
      </CardHeader>
      <CardContent className="p-3">
        <div className="text-xl font-bold leading-none">{value}</div>
        <p className="text-xs text-muted-foreground mt-0.5 leading-tight">{note}</p>
      </CardContent>
    </Card>
  )
}

/** Closed-won revenue by account for a period (any user). */
function BookingsView({ filters }: { filters: ReportFilterValues }) {
  const [limit, setLimit] = useState('25')
  const { data, isLoading, error } = useTopAccounts({
    limit: parseInt(limit, 10),
    ...(filters.owner_id ? { owner_id: filters.owner_id } : {}),
    ...(filters.startDate ? { startDate: filters.startDate } : {}),
    ...(filters.endDate ? { endDate: filters.endDate } : {}),
  })
  const rows = useMemo<TopAccountRow[]>(() => data?.byOpportunityRevenue ?? [], [data])
  const summary: TopAccountsSummary | undefined = data?.summary
  const period = periodText(data?.period)

  const chartData = useMemo(
    () =>
      rows.slice(0, 10).map((r) => ({
        name: r.name?.length > 20 ? `${r.name.substring(0, 20)}…` : r.name,
        revenue: Number(r.closed_won_revenue) || 0,
      })),
    [rows]
  )
  const pieData = summary
    ? [
        { name: 'Top 10 accounts', value: summary.top10_revenue },
        { name: 'All other accounts', value: summary.total_revenue - summary.top10_revenue },
      ]
    : []

  const handleExportCSV = () =>
    exportToCSV({
      filename: 'top-accounts.csv',
      data: rows.map((r, i) => ({
        Rank: i + 1,
        'Account Name': r.name,
        Owner: r.owner_name ?? '',
        'Closed-won revenue (CAD)': Number(r.closed_won_revenue) || 0,
        'Deals won': r.won_count,
        'Avg Deal Size (CAD)': r.won_count ? (Number(r.closed_won_revenue) || 0) / r.won_count : 0,
        'Open pipeline (CAD)': Number(r.pipeline_value) || 0,
        'Win rate (%)': r.win_rate ?? '',
      })),
    })

  if (error)
    return (
      <Card>
        <CardContent className="pt-6 space-y-3">
          <p className="text-destructive font-medium">
            The Top Accounts report couldn&apos;t load.
          </p>
          <p className="text-sm text-muted-foreground">
            {explainError(error, 'Reload the page to try again.')}
          </p>
          <Button onClick={() => window.location.reload()} variant="outline">
            Try again
          </Button>
        </CardContent>
      </Card>
    )

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="space-y-1">
          <Label htmlFor="limit">Show</Label>
          <Select value={limit} onValueChange={setLimit}>
            <SelectTrigger id="limit" className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="10">Top 10</SelectItem>
              <SelectItem value="25">Top 25</SelectItem>
              <SelectItem value="50">Top 50</SelectItem>
              <SelectItem value="100">Top 100</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button
          variant="outline"
          onClick={handleExportCSV}
          disabled={!rows.length}
          aria-label="Export top accounts to CSV"
        >
          <Download className="mr-2 h-4 w-4" aria-hidden="true" />
          Export CSV
        </Button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2
            className="h-8 w-8 animate-spin text-muted-foreground"
            aria-label="Loading top accounts"
          />
        </div>
      ) : !rows.length ? (
        <Card
          style={{ backgroundColor: SEMANTIC.warning.fill, borderColor: SEMANTIC.warning.fill }}
        >
          <CardContent className="pt-6">
            <p className="font-medium" style={{ color: SEMANTIC.warning.text }}>
              No closed-won revenue in this period
            </p>
            <p className="text-sm" style={{ color: SEMANTIC.warning.text }}>
              Try a longer period or another rep.
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="grid gap-4 md:grid-cols-4">
            <StatCard
              title="Closed-won revenue"
              value={formatCurrency(summary?.total_revenue || 0, 'CAD')}
              note={period}
              icon={DollarSign}
              color={CATEGORICAL.evergreen.solid}
            />
            <StatCard
              title="Top 10 share"
              value={`${(summary?.top10_share_pct ?? 0).toFixed(1)}%`}
              note="of all closed-won revenue in the period"
              icon={TrendingUp}
              color={CATEGORICAL.teal.solid}
            />
            <StatCard
              title="Accounts"
              value={(summary?.total_accounts ?? 0).toLocaleString()}
              note="with closed-won revenue in the period"
              icon={Users}
              color={CATEGORICAL.plum.solid}
            />
            <StatCard
              title="Avg per account"
              value={formatCurrency(summary?.avg_revenue_per_account || 0, 'CAD')}
              note={`average deal ${formatCurrency(summary?.avg_deal_size || 0, 'CAD')}`}
              icon={Building2}
              color={CATEGORICAL.clay.solid}
            />
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Top 10 accounts by revenue</CardTitle>
                <CardDescription>Closed-won revenue, {period}</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={chartData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis dataKey="name" type="category" width={150} />
                    <Tooltip
                      content={<ChartTooltipContent />}
                      formatter={(value: number) => formatCurrency(value, 'CAD')}
                    />
                    <Legend />
                    <Bar dataKey="revenue" fill={COLORS[0]} name="Revenue" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Revenue concentration</CardTitle>
                <CardDescription>Top 10 accounts against everyone else, {period}</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={(entry) =>
                        `${entry.name}: ${
                          summary?.total_revenue
                            ? ((entry.value / summary.total_revenue) * 100).toFixed(1)
                            : '0'
                        }%`
                      }
                      outerRadius={80}
                      dataKey="value"
                    >
                      {pieData.map((_e, i) => (
                        <Cell key={i} fill={COLORS[i % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      content={<ChartTooltipContent />}
                      formatter={(value: number) => formatCurrency(value, 'CAD')}
                    />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Account rankings</CardTitle>
              <CardDescription>
                Closed-won revenue, {period}. Open pipeline is as of today.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" aria-label="Top accounts rankings">
                  <thead>
                    <tr className="border-b">
                      <th scope="col" className="text-left p-2 font-medium">
                        Rank
                      </th>
                      <th scope="col" className="text-left p-2 font-medium">
                        Account
                      </th>
                      <th scope="col" className="text-left p-2 font-medium">
                        Owner
                      </th>
                      <th scope="col" className="text-right p-2 font-medium">
                        Closed-won (CAD)
                      </th>
                      <th scope="col" className="text-right p-2 font-medium">
                        Deals won
                      </th>
                      <th scope="col" className="text-right p-2 font-medium">
                        Avg deal (CAD)
                      </th>
                      <th scope="col" className="text-right p-2 font-medium">
                        Open pipeline (CAD)
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => {
                      const revenue = Number(r.closed_won_revenue) || 0
                      return (
                        <tr key={r.id} className="border-b hover:bg-muted/50">
                          <td className="p-2">{i + 1}</td>
                          <td className="p-2">
                            <Link to={`/accounts/${r.id}`} className="text-primary underline">
                              {r.name}
                            </Link>
                          </td>
                          <td className="p-2 text-muted-foreground">{r.owner_name ?? '–'}</td>
                          <td className="text-right p-2 tabular-nums">
                            {formatCurrency(revenue, 'CAD')}
                          </td>
                          <td className="text-right p-2 tabular-nums">{r.won_count}</td>
                          <td className="text-right p-2 tabular-nums">
                            {formatCurrency(r.won_count ? revenue / r.won_count : 0, 'CAD')}
                          </td>
                          <td className="text-right p-2 tabular-nums">
                            {formatCurrency(Number(r.pipeline_value) || 0, 'CAD')}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  )
}

export default function TopAccounts() {
  const { isAdmin } = useUser()
  const [filters, setFilters] = useState<ReportFilterValues>({})
  const [tab, setTab] = useState('bookings')

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Top Accounts</h1>
          <p className="text-muted-foreground">
            {isAdmin
              ? 'Highest-value accounts by closed-won revenue, and billed spend against last year'
              : 'Highest-value accounts by closed-won revenue'}
          </p>
        </div>
        <ReportFilters
          value={filters}
          onChange={setFilters}
          defaultPeriod="all"
          showPeriod={tab === 'bookings'}
          datePresets={[
            'this_month',
            'this_quarter',
            'fiscal_year',
            'calendar_year',
            'last_12_months',
            'all',
            'custom',
          ]}
        />
      </div>

      {isAdmin ? (
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="bookings">Bookings (closed won)</TabsTrigger>
            <TabsTrigger value="billed">Billed spend vs last year</TabsTrigger>
          </TabsList>
          <TabsContent value="bookings" className="mt-4">
            <BookingsView filters={filters} />
          </TabsContent>
          <TabsContent value="billed" className="mt-4">
            <SpendTracker ownerId={filters.owner_id} />
          </TabsContent>
        </Tabs>
      ) : (
        <BookingsView filters={filters} />
      )}
    </div>
  )
}
